import React, { useState } from 'react';
import {
  Grid, Button, Collapse,
  Dialog, DialogActions,
  DialogContent, DialogTitle, Divider,
  IconButton,
  Tooltip, Typography
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { AddCircle, Delete, Edit } from '@material-ui/icons';
import ArrowDownwardRoundedIcon from '@material-ui/icons/ArrowDownwardRounded';
import { useTranslation } from 'react-i18next';

import UpdateKPIEntry from './UpdateKPIEntry';
import CreateKPIEntry from './CreateKPIEntry';
import UpdateKPI from './UpdateKPI';
import DeleteKPI from './DeleteKPI';
import DeleteKPIEntry from './DeleteKPIEntry';
import KpiEventLog from './KpiEventLog';
import { formatDate } from '../../../utils/dates';
import KPIProgressIndicator from './KPIProgressIndicator';
import Linkify from '../../Linkify';
import { STATUS } from '../../../utils/const';

const ObjectiveKPIDetails = ({
  objective,
  kpi,
  progress,
  prorate,
  onSubmitSuccess,
  editable
}) => {
  const { t } = useTranslation();

  const { accountInfo, employee } = useSelector(state => state);

  const employees = [
    ...accountInfo.possibleAppraisers,
    employee
  ];

  const [ open, setOpen ] = useState(false);
  const [ state, setState ] = useState('details');
  const [ selectedEntry, setSelectedEntry ] = useState(null);

  const hasEntries = !!kpi.entries.length;

  const resetState = () => {
    setState('details');
    setSelectedEntry(null);
  };

  const onClose = () => {
    setOpen(false);
    resetState();
  };

  const addKpiEntryTooltipTitle = STATUS.ACTIVE === objective.status ?
    t('objective.kpi.actions.entryCreate ') : t('objective.kpi.entry.error.status.create');

  const editKpiEntryTooltipTitle = STATUS.ACTIVE === objective.status ?
    t('objective.kpi.actions.entryUpdate') : t('objective.kpi.entry.error.status.update');

  const deleteKpiEntryTooltipTitle = STATUS.ACTIVE === objective.status ?
    t('objective.kpi.actions.entryDelete') : t('objective.kpi.entry.error.status.delete');

  return <>
    <Tooltip title={ t('objective.kpi.actions.details') }>
      <IconButton onClick={ () => setOpen(true) } color='primary'>
        <AddCircle />
      </IconButton>
    </Tooltip>
    <Dialog
      open={ open }
      onClose={ onClose }
      maxWidth='md'
      fullWidth
    >
      <DialogTitle>
        {t(`objective.kpi.title.${state}`)}
      </DialogTitle>
      <DialogContent>
        <Collapse direction='left' in={ state === 'details' } mountOnEnter unmountOnExit>
          <Grid container direction='row' style={ { marginBottom: 8 } }>
            <Grid container item xs={ 8 } sm={ 4 } alignItems='center'>{kpi.name}</Grid>
            <Grid container item xs={ 4 } sm={ 2 } alignItems='center'>
              {kpi.goalValue} {kpi.unit}
            </Grid>
            <Grid container item xs={ 5 } sm={ 3 }  alignItems='center'>
              {formatDate(new Date(kpi.deadline))}
            </Grid>
            <Grid item xs={ 5 } sm={ 2 }>
              <KPIProgressIndicator prorate={ prorate } progress={ progress } />
            </Grid>
            {editable &&
                <Grid container item xs={ 2 } sm={ 1 } justifyContent='flex-end'>
                  <Tooltip title={ t('objective.kpi.actions.update') }>
                    <IconButton onClick={ () => setState('update') }>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                </Grid>
            }
          </Grid>
          {editable &&
              <div
                style={ {
                  display: 'flex',
                  justifyContent: 'flex-end'
                } }
              >
                <Tooltip title={ hasEntries ? t('objective.kpi.deleteRestriciton') : '' }>
                  <div>
                    <Button
                      onClick={ () => {
                        setState('delete');
                      } }
                      variant='contained'
                      style={ hasEntries ? undefined : { background: '#ff8a80' } }
                      disabled={ hasEntries }
                    >
                      {t('objective.action.delete')}
                    </Button>
                  </div>
                </Tooltip>
              </div>
          }
          <Divider style={ { margin: '32px 0 16px', background: '#aaa' } } />
          <Typography style={ { fontSize: 18, marginBottom: 8 } }>
            {t('objective.kpi.entries')}
          </Typography>
          {kpi.entries.map((entry, index) =>
            <React.Fragment key={ entry.objective_kpi_entry_id }>
              <Grid container direction='row'>
                <Grid item xs={ 8 } sm={ 2 }>
                  {entry.value} {kpi.unit}
                </Grid>
                <Grid item xs={ 4 } sm={ 4 }>
                  <Linkify>
                    {entry.comment}
                  </Linkify>
                </Grid>
                <Grid item xs={ 5 } sm={ 3 }>
                  {formatDate(new Date(entry.date))}
                </Grid>
                <Grid item xs={ 4 } sm={ 2 }>
                  {employees.find(emp => Number(emp.empid) === entry.employee_id)?.name}
                </Grid>
                { editable &&
                    <Grid container item xs={ 3 } sm={ 1 } justifyContent='flex-end'>
                      <Tooltip title={ editKpiEntryTooltipTitle }>
                        <span>
                          <IconButton size='small' onClick={ () => {
                            setState('entryUpdate');
                            setSelectedEntry(entry);
                          } }
                          disabled={ STATUS.ACTIVE !== objective.status }
                          >
                            <Edit />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={ deleteKpiEntryTooltipTitle }>
                        <span>
                          <IconButton size='small' onClick={ () => {
                            setState('entryDelete');
                            setSelectedEntry(entry);
                          } }
                          disabled={ STATUS.ACTIVE !== objective.status }
                          >
                            <Delete />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                }
              </Grid>
              {kpi.entries.length - 1 > index && <Divider style={ { margin: 4 } } />}
            </React.Fragment>
          )}
          {editable &&
            <div style={ { display: 'flex', justifyContent: 'center' } }>
              <Tooltip title={ addKpiEntryTooltipTitle }>
                <span>
                  <IconButton
                    onClick={ () => setState('entryCreate') }
                    color='primary'
                    disabled={ STATUS.ACTIVE !== objective.status }
                  >
                    <AddCircle />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          }
          <Divider style={ { margin: '16px 0', background: '#aaa' } } />
          <Typography style={ { fontSize: 18, marginBottom: 8 } }>
            {t('objective.button.tab.eventLog')}
          </Typography>
        </Collapse>
        <div style={ { display: 'flex', justifyContent: 'center' } }>
          <Tooltip title={  t('objective.kpi.actions.openEventLog ') }>
            <span>
              <IconButton
                onClick={ () => setState('eventLog') }
                color='primary'
              >
                <ArrowDownwardRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>
        <Collapse direction='left' in={ state === 'update' } mountOnEnter unmountOnExit>
          <UpdateKPI
            kpi={ kpi }
            objective={ objective }
            onSubmitSuccess={
              () => {
                onSubmitSuccess?.();
                resetState();
              }
            }
            onCancel={ resetState }
          />
        </Collapse>
        <Collapse direction='left' in={ state === 'delete' } mountOnEnter unmountOnExit>
          <DeleteKPI
            kpi={ kpi }
            objective={ objective }
            onSubmitSuccess={
              () => {
                onSubmitSuccess?.();
                resetState();
              }
            }
            onCancel={ resetState }
          />
        </Collapse>
        <Collapse direction='left' in={ state === 'entryCreate' } mountOnEnter unmountOnExit>
          <CreateKPIEntry
            kpi={ kpi }
            objective={ objective }
            onSubmitSuccess={ () => {
              onSubmitSuccess?.();
              resetState();
            } }
            onCancel={ resetState }
          />
        </Collapse>
        <Collapse direction='left' in={ state === 'entryUpdate' } mountOnEnter unmountOnExit>
          <UpdateKPIEntry
            objective={ objective }
            kpi={ kpi }
            kpiEntry={ selectedEntry }
            onSubmitSuccess={ () => {
              onSubmitSuccess?.();
              resetState();
            } }
            onCancel={ resetState }
          />
        </Collapse>
        <Collapse direction='left' in={ state === 'entryDelete' } mountOnEnter unmountOnExit>
          <DeleteKPIEntry
            objective={ objective }
            kpi={ kpi }
            kpiEntry={ selectedEntry }
            onSubmitSuccess={ () => {
              onSubmitSuccess?.();
              resetState();
            } }
            onCancel={ resetState }
          />
        </Collapse>
        <Collapse direction='left' in={ state === 'eventLog' } mountOnEnter unmountOnExit>
          <KpiEventLog
            onCancel={ resetState }
            objective={ objective }
            kpi={ kpi }
          />
        </Collapse>
        <Divider style={ { marginTop: 8 } }/>
      </DialogContent>
      <DialogActions>
        <Button onClick={ onClose } color='primary'>
          { t('objective.todos.dialog.close.btn') }
        </Button>
      </DialogActions>
    </Dialog>
  </>;
};

export default ObjectiveKPIDetails;
